/**
 * SeriesDetailScreen: a series' page, shared by the Mini App's `/hub/series/:seriesId` and the
 * studio website's `/series/:seriesId/:nameSlug?` (shared-client-screens D5, Decision A; card 3
 * row H8, card 2 row H7). Props only: each side reads its own series and hands in Back and what
 * the button does. On a computer the website shows the same screen in its centered column.
 *
 * Shows: the image or a themed fallback, the title with the client's booking badge, every date
 * in the series' own zone, the length and the whole package's price, the place, the spots left,
 * the practitioner and the description. The button: View booking, Sold Out, Registration Closed,
 * Complete current booking first (the app inside another booking), or Book This Series.
 */
import { InitialsAvatar } from '../../components/hub/InitialsAvatar';
import { ThemedFallback } from '../../components/hub/ThemedFallback';
import { FocalImage } from '../../components/FocalImage';
import { SafeRichText } from '../../components/booking/SafeRichText';
import type { UserBookingInfo } from '../../api/types';
import { isFullyBooked } from '../../utils/availability';
import { formatPrice } from '../../utils/formatPrice';
import styles from './SeriesDetailPage.module.css';
import { virtualPlatformLabel } from '@telify/shared';

export interface SeriesDetailContent {
  title: string;
  imageUrl: string | null;
  /** The framing the studio set on that photo; null for centered. */
  focalPointX: number | null;
  focalPointY: number | null;
  focalPointScale: number | null;
  /** Each date's start, ISO 8601; read in the viewer's own zone (Gabriel 2026-11-02). */
  dates: string[];
  /** Each date's length. */
  durationMinutes: number;
  /** The whole package's price in dollars; null or 0 is free. */
  totalPrice: number | null;
  spotsRemaining: number;
  isBookable: boolean;
  location: { name: string | null; city: string | null; state: string | null; isVirtual: boolean; virtualPlatform: string | null };
  practitioner: { name: string; photoUrl: string | null; specialty: string | null };
  description: string | null;
  /** The client's own booking on it; null for none (always null for a visitor). */
  userBooking: UserBookingInfo | null;
}

export interface SeriesDetailScreenProps {
  status: 'loading' | 'notfound' | 'error' | 'ready';
  /** ready: the series. */
  series?: SeriesDetailContent;
  /** error: the line under "Something went wrong". */
  errorMessage?: string;
  onRetry: () => void;
  onBack: () => void;
  onViewBooking: (bookingId: string) => void;
  /** Book This Series: `reserving` while the app holds the spot; `enabled` false while another booking is open in the app. */
  reserve: { onReserve: () => void; reserving: boolean; enabled: boolean };
}

/**
 * Get spots display info with conditional visibility and color variants.
 * - > 5 spots: hidden
 * - 4-5 spots: yellow (warning)
 * - 1-3 spots: red (urgent)
 * - 0 spots: "Sold out" in muted gray
 */
function getSpotsDisplay(spotsRemaining: number): {
  show: boolean;
  text: string;
  variant: 'yellow' | 'red' | 'muted';
} {
  const spotWord = spotsRemaining === 1 ? 'spot' : 'spots';
  if (spotsRemaining === 0) {
    return { show: true, text: 'Sold out', variant: 'muted' };
  }
  if (spotsRemaining <= 3) {
    return { show: true, text: `${spotsRemaining} ${spotWord}`, variant: 'red' };
  }
  if (spotsRemaining <= 5) {
    return { show: true, text: `${spotsRemaining} ${spotWord}`, variant: 'yellow' };
  }
  return { show: false, text: '', variant: 'yellow' };
}

/**
 * Get human-readable label for booking status.
 */
function getBookingStatusLabel(status: string): string {
  switch (status) {
    case 'CONFIRMED': return 'Confirmed';
    case 'PENDING_APPROVAL': return 'Pending Approval';
    case 'PENDING_PAYMENT': return 'Pending Payment';
    case 'WAITLISTED': return 'Waitlisted';
    default: return status;
  }
}

/** Statuses that allow viewing the booking details. */
const VIEWABLE_BOOKING_STATUSES = ['CONFIRMED', 'PENDING_PAYMENT', 'PENDING_APPROVAL', 'WAITLISTED'];

/**
 * Check if user can view their booking.
 */
function canViewBooking(userBooking: UserBookingInfo | null): userBooking is UserBookingInfo {
  return userBooking !== null && VIEWABLE_BOOKING_STATUSES.includes(userBooking.status);
}

const BACK_LABEL = 'Back to Events';

function BackHeader({ onBack }: { onBack: () => void }) {
  return (
    <header className={styles.header}>
      <button className={styles.backBtn} onClick={onBack} aria-label={BACK_LABEL}>
        <ArrowLeftIcon />
        <span>{BACK_LABEL}</span>
      </button>
    </header>
  );
}

export function SeriesDetailScreen({ status, series, errorMessage, onRetry, onBack, onViewBooking, reserve }: SeriesDetailScreenProps) {
  if (status === 'notfound' || status === 'error') {
    const is404 = status === 'notfound';
    return (
      <div className={styles.page}>
        <BackHeader onBack={onBack} />
        <div className={styles.errorContainer}>
          <h2 className={styles.errorTitle}>{is404 ? 'Series Not Found' : 'Something went wrong'}</h2>
          <p className={styles.errorMessage}>
            {is404 ? 'This series may have been removed or is no longer available.' : errorMessage || 'Failed to load series'}
          </p>
          {!is404 && (
            <button className={styles.retryBtn} onClick={onRetry}>
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (status === 'loading' || !series) return <SeriesDetailSkeleton />;

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const count = series.dates.length;
  const booking = canViewBooking(series.userBooking) ? series.userBooking : null;
  const { location } = series;
  const spotsInfo = getSpotsDisplay(series.spotsRemaining);

  return (
    <div className={styles.page}>
      <BackHeader onBack={onBack} />

      <section className={styles.detailSection}>
        <div className={styles.imageContainer}>
          {series.imageUrl ? (
            <FocalImage
              src={series.imageUrl}
              alt={series.title}
              className={styles.image}
              focalPointX={series.focalPointX}
              focalPointY={series.focalPointY}
              focalPointScale={series.focalPointScale}
            />
          ) : (
            <ThemedFallback type="event" className={styles.imageFallback} />
          )}
        </div>

        <div className={styles.detailContent}>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>{series.title}</h2>
            {booking && (
              <span className={styles.bookingStatusBadge} data-status={booking.status}>
                {getBookingStatusLabel(booking.status)}
              </span>
            )}
          </div>

          <div className={styles.instanceDatesSection}>
            <p className={styles.instanceDatesLabel}>
              {count} {count === 1 ? 'session' : 'sessions'}
            </p>
            <div className={styles.instanceDatesList}>
              {series.dates.map((dateStr, idx) => (
                <div key={idx} className={styles.instanceDateItem}>
                  <CalendarIcon />
                  <span>{dateFormatter.format(new Date(dateStr))}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.meta}>
            <div className={styles.metaRow}>
              <ClockIcon />
              <span>{series.durationMinutes} min per session</span>
            </div>
            <div className={styles.metaRow}>
              <DollarIcon />
              <span>
                {series.totalPrice !== null && series.totalPrice !== 0
                  ? `${formatPrice(series.totalPrice)} total \u00B7 ${count} sessions`
                  : 'Free'}
              </span>
            </div>
          </div>

          {(location.name || location.isVirtual) && (
            <div className={styles.locationRow}>
              <LocationIcon />
              <div className={styles.locationText}>
                {location.isVirtual ? (
                  <span>{virtualPlatformLabel(location.virtualPlatform) ? `Online on ${virtualPlatformLabel(location.virtualPlatform)}` : 'Online'}</span>
                ) : (
                  <>
                    {location.name && <span>{location.name}</span>}
                    {(location.city || location.state) && (
                      <span className={styles.locationSecondary}>{[location.city, location.state].filter(Boolean).join(', ')}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {spotsInfo.show && series.spotsRemaining !== 0 && (
            <div className={styles.spotsRow}>
              <span className={styles.spotsText} data-variant={spotsInfo.variant}>
                {spotsInfo.text}
              </span>
            </div>
          )}

          <div className={styles.practitionerRow}>
            <InitialsAvatar name={series.practitioner.name} photoUrl={series.practitioner.photoUrl} size="sm" />
            <div className={styles.practitionerInfo}>
              <span className={styles.practitionerName}>{series.practitioner.name}</span>
              {series.practitioner.specialty && <span className={styles.practitionerSpecialty}>{series.practitioner.specialty}</span>}
            </div>
          </div>

          {series.description && <SafeRichText html={series.description} className={styles.description} />}

          <SeriesCta series={series} booking={booking} onViewBooking={onViewBooking} reserve={reserve} />
        </div>
      </section>
    </div>
  );
}

function SeriesCta({
  series,
  booking,
  onViewBooking,
  reserve,
}: {
  series: SeriesDetailContent;
  booking: UserBookingInfo | null;
  onViewBooking: (bookingId: string) => void;
  reserve: SeriesDetailScreenProps['reserve'];
}) {
  if (booking) {
    return (
      <button className={`${styles.ctaBtn} ${styles.ctaBtnViewBooking}`} onClick={() => onViewBooking(booking.id)}>
        <span>View booking</span>
      </button>
    );
  }
  if (isFullyBooked(series.spotsRemaining)) return <div className={styles.soldOutIndicator}>Sold Out</div>;
  if (!series.isBookable) {
    return (
      <button className={styles.ctaBtn} disabled aria-disabled="true">
        <span>Registration Closed</span>
      </button>
    );
  }
  if (!reserve.enabled) {
    return (
      <button className={styles.ctaBtn} disabled aria-disabled="true">
        <span>Complete current booking first</span>
      </button>
    );
  }
  return (
    <button
      className={`${styles.ctaBtn} ${styles.ctaBtnEnabled}`}
      disabled={reserve.reserving}
      aria-disabled={reserve.reserving}
      onClick={reserve.onReserve}
    >
      <span>{reserve.reserving ? 'Reserving your spot...' : 'Book This Series'}</span>
    </button>
  );
}

// ============================================
// Skeleton
// ============================================

function SeriesDetailSkeleton() {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.skeletonBackBtn} />
      </header>
      <section className={styles.detailSection}>
        <div className={styles.skeletonImage} />
        <div className={styles.detailContent}>
          <div className={styles.skeletonTitle} />
          <div className={styles.skeletonMeta} />
          <div className={styles.skeletonMeta} />
          <div className={styles.skeletonMeta} />
          <div className={styles.skeletonDescription} />
          <div className={styles.skeletonBtn} />
        </div>
      </section>
    </div>
  );
}

// ============================================
// Icons
// ============================================

function ArrowLeftIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function DollarIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
